import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import {
    FaTimes, FaEye, FaEdit, FaTrash, FaCalendar,
    FaUsers, FaBullseye, FaChartLine, FaGamepad,
    FaEnvelope, FaWhatsapp, FaGift, FaRobot
} from 'react-icons/fa';
import Swal from 'sweetalert2';

const CampaignDetailModal = ({ isOpen, onClose, campaign, onDelete, onEdit }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && campaign) {
            fetchCampaignStats();
        }
    }, [isOpen, campaign]);

    const fetchCampaignStats = async () => {
        if (!campaign) return;

        setLoading(true);
        try {
            let endpoint = '';
            switch (campaign.tipo) {
                case 'game-loyalty':
                    endpoint = `game-loyalty/${campaign._id}/stats`;
                    break;
                case 'referral':
                    endpoint = `referral-campaign/${campaign._id}/stats`;
                    break;
                case 'ai-marketing':
                    endpoint = `agents/marketing/campaigns/${campaign._id}/stats`;
                    break;
                default:
                    endpoint = `campaigns/${campaign._id}/stats`;
            }

            const response = await get(endpoint);
            setStats(response);
        } catch (error) {
            console.warn('No se pudieron cargar las estadísticas:', error);
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    const getCampaignIcon = (tipo) => {
        switch (tipo) {
            case 'game-loyalty':
                return <FaGamepad className="text-purple-500" />;
            case 'referral':
                return <FaUsers className="text-blue-500" />;
            case 'ai-marketing':
                return <FaRobot className="text-green-500" />;
            default:
                return <FaBullseye className="text-gray-500" />;
        }
    };

    const getCampaignTypeText = (tipo) => {
        switch (tipo) {
            case 'game-loyalty':
                return 'Juego y Fidelización';
            case 'referral':
                return 'Referidos';
            case 'ai-marketing':
                return 'IA Marketing';
            default:
                return 'Campaña';
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
            case 'activa':
                return 'text-green-600 bg-green-100';
            case 'draft':
            case 'borrador':
                return 'text-yellow-600 bg-yellow-100';
            case 'completed':
            case 'completada':
                return 'text-blue-600 bg-blue-100';
            case 'paused':
            case 'pausada':
                return 'text-orange-600 bg-orange-100';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPercentage = (value) => {
        if (!value) return '0%';
        return `${(value * 100).toFixed(1)}%`;
    };

    if (!isOpen || !campaign) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center">
                        {getCampaignIcon(campaign.tipo)}
                        <div className="ml-3">
                            <h2 className="text-xl font-bold text-gray-800">
                                {campaign.nombre || campaign.name}
                            </h2>
                            <p className="text-sm text-gray-600">
                                {getCampaignTypeText(campaign.tipo)}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.estado || campaign.status)}`}>
                            {campaign.estado || campaign.status || 'DRAFT'}
                        </span>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Información básica */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Información de la Campaña</h3>
                            <div className="space-y-3">
                                <div>
                                    <span className="text-sm text-gray-600">Descripción:</span>
                                    <p className="text-gray-800 mt-1">
                                        {campaign.descripcion || campaign.description || 'Sin descripción'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-600">Fecha de creación:</span>
                                    <p className="text-gray-800 mt-1">
                                        {formatDate(campaign.fechaCreacion || campaign.createdAt)}
                                    </p>
                                </div>
                                {campaign.tipoCampaign && (
                                    <div>
                                        <span className="text-sm text-gray-600">Tipo específico:</span>
                                        <p className="text-gray-800 mt-1">{campaign.tipoCampaign}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Estadísticas */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Estadísticas</h3>
                            {loading ? (
                                <div className="animate-pulse space-y-3">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="h-8 bg-gray-200 rounded"></div>
                                    ))}
                                </div>
                            ) : stats ? (
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Participaciones:</span>
                                        <span className="text-sm font-semibold">
                                            {stats.participations || stats.totalParticipations || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Alcance:</span>
                                        <span className="text-sm font-semibold">
                                            {stats.scope || stats.reach || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Conversiones:</span>
                                        <span className="text-sm font-semibold text-green-600">
                                            {stats.conversions || stats.totalConversions || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Tasa de apertura:</span>
                                        <span className="text-sm font-semibold">
                                            {formatPercentage(stats.openRate || stats.averageOpenRate)}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No hay estadísticas disponibles</p>
                            )}
                        </div>
                    </div>

                    {/* Configuración específica por tipo */}
                    {campaign.tipo === 'game-loyalty' && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuración del Juego</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Puntos por participación</div>
                                    <div className="text-lg font-semibold text-purple-600">
                                        {campaign.puntosPorParticipacion || 0}
                                    </div>
                                </div>
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Total de participaciones</div>
                                    <div className="text-lg font-semibold text-blue-600">
                                        {campaign.totalParticipations || 0}
                                    </div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Participaciones actuales</div>
                                    <div className="text-lg font-semibold text-green-600">
                                        {campaign.participations?.length || 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {campaign.tipo === 'ai-marketing' && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuración de IA</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Objetivo</div>
                                    <div className="text-lg font-semibold text-green-600">
                                        {campaign.objective || campaign.objetivo || 'N/A'}
                                    </div>
                                </div>
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600">Canal</div>
                                    <div className="text-lg font-semibold text-blue-600">
                                        {campaign.channel || campaign.canal || 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Acciones */}
                    <div className="flex justify-end space-x-3 pt-6 border-t">
                        <button
                            onClick={() => onEdit && onEdit(campaign)}
                            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            <FaEdit className="mr-2" />
                            Editar
                        </button>
                        <button
                            onClick={() => {
                                Swal.fire({
                                    title: '¿Estás seguro?',
                                    text: 'Esta acción no se puede deshacer.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#d33',
                                    confirmButtonText: 'Sí, eliminar',
                                    cancelButtonText: 'Cancelar',
                                }).then((result) => {
                                    if (result.isConfirmed && onDelete) {
                                        onDelete(campaign);
                                    }
                                });
                            }}
                            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                            <FaTrash className="mr-2" />
                            Eliminar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignDetailModal; 